"use client";

import { useEffect } from "react";

import { env } from "~/env.mjs";
import { useStore } from "~/hooks/store";
import { PHProvider } from "~/providers/posthog-provider";
import { AnalyticsContext } from "./AnalyticsContextState";
import { type Analytics } from "./AnalyticsType";
import { LocalAnalytics } from "./LocalAnalytics";
import { PosthogAnalytics } from "./PosthogAnalytics";
import PostHogPageView from "./posthog-pageview";

export const analytics: Analytics = env.NEXT_PUBLIC_POSTHOG_KEY
  ? new PosthogAnalytics()
  : new LocalAnalytics();

export const AnalyticsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const loggedUser = useStore((s) => s.loggedUser);

  useEffect(() => {
    // Never identify anonymous users: person_profiles is "identified_only",
    // so an identify() call is what promotes a throwaway session into a real
    // PostHog person — complete with its @temp_anon.xyz email. Leave them as
    // unidentified events so funnels and person lists stay clean.
    if (loggedUser && !loggedUser.isAnonymous) {
      analytics.setUser(loggedUser.uid, {
        email: loggedUser.email,
        username: loggedUser.displayName,
      });
    }
  }, [loggedUser]);

  return (
    <PHProvider>
      <PostHogPageView />
      <AnalyticsContext.Provider value={analytics}>
        {children}
      </AnalyticsContext.Provider>
    </PHProvider>
  );
};
